import { useState } from 'react'
import { drawPanorama, drawStamp } from '../utils/drawUtils'

function ExportPanel({ fullConfig, config, updateConfig }) {
  const [exporting, setExporting] = useState(false)
  const [progress, setProgress] = useState(0)

  /**
   * Compute export dimensions from the window aspect ratio and quality preset.
   * The window config uses width/height in vw — their ratio gives us the aspect.
   */
  const getExportDimensions = () => {
    const aspect = (fullConfig?.window?.width || 92) / (fullConfig?.window?.height || 46)
    switch (config.quality) {
      case '720p': {
        const h = 720
        return { width: Math.round(h * aspect / 2) * 2, height: h }
      }
      case '1080p': {
        const h = 1080
        return { width: Math.round(h * aspect / 2) * 2, height: h }
      }
      case '4k': {
        const h = 2160
        return { width: Math.round(h * aspect / 2) * 2, height: h }
      }
      case 'full': {
        // Use live canvas size × 2
        const frame = document.querySelector('.envelope-frame')
        if (frame) {
          const rect = frame.getBoundingClientRect()
          return { width: Math.round(rect.width * 2), height: Math.round(rect.height * 2) }
        }
        return { width: 1920, height: 960 }
      }
      default: return { width: 1920, height: 960 }
    }
  }

  /**
   * Render one frame onto exportCtx at the given frame index,
   * completely offline (no dependence on the live canvas).
   */
  const renderFrame = (exportCtx, frameIndex, fps, dims) => {
    const { width, height } = dims
    const cfg = fullConfig

    // Calculate offsets for this frame based on speed and frame number
    const bgOffset = cfg.background.speed * frameIndex * (60 / fps)
    const fgOffset = cfg.foreground.speed * frameIndex * (60 / fps)

    // Clear
    exportCtx.clearRect(0, 0, width, height)

    // Background
    if (cfg.background.image) {
      drawPanorama(
        exportCtx, cfg.background.image,
        bgOffset, cfg.background.direction,
        width, height,
        cfg.background.motionBlur || 0
      )
    }

    // Foreground
    if (cfg.foreground.image) {
      drawPanorama(
        exportCtx, cfg.foreground.image,
        fgOffset, cfg.foreground.direction,
        width, height,
        cfg.foreground.motionBlur || 0
      )
    }

    // Stamps
    if (cfg.stamps?.length) {
      cfg.stamps.forEach(stamp => drawStamp(exportCtx, stamp, width, height))
    }
  }

  const handleExportVideo = async () => {
    if (!fullConfig) return
    setExporting(true)
    setProgress(0)

    try {
      const dims = getExportDimensions()
      const fps = config.fps || 30
      const totalFrames = config.duration * fps

      const exportCanvas = document.createElement('canvas')
      exportCanvas.width = dims.width
      exportCanvas.height = dims.height
      const exportCtx = exportCanvas.getContext('2d')

      // MediaRecorder setup
      const stream = exportCanvas.captureStream(fps)
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: config.quality === '4k' ? 20000000 : 8000000
      })

      const chunks = []
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data)
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `panorama-${dims.width}x${dims.height}-${Date.now()}.webm`
        a.click()
        URL.revokeObjectURL(url)
        setExporting(false)
        setProgress(100)
      }

      mediaRecorder.start()

      let currentFrame = 0
      const frameDuration = 1000 / fps

      const captureFrame = () => {
        if (currentFrame >= totalFrames) {
          mediaRecorder.stop()
          return
        }

        renderFrame(exportCtx, currentFrame, fps, dims)
        currentFrame++
        setProgress(Math.round((currentFrame / totalFrames) * 100))

        // Use setTimeout to maintain precise frame timing
        setTimeout(captureFrame, frameDuration)
      }

      captureFrame()
    } catch (error) {
      console.error('Export error:', error)
      alert('Export failed: ' + error.message)
      setExporting(false)
    }
  }

  const handleExportImages = async () => {
    if (!fullConfig) return
    setExporting(true)
    setProgress(0)

    try {
      const dims = getExportDimensions()
      const fps = config.fps || 30
      const totalFrames = config.duration * fps

      const exportCanvas = document.createElement('canvas')
      exportCanvas.width = dims.width
      exportCanvas.height = dims.height
      const exportCtx = exportCanvas.getContext('2d')

      let currentFrame = 0

      const captureFrame = () => {
        if (currentFrame >= totalFrames) {
          setExporting(false)
          setProgress(100)
          alert(`Exported ${totalFrames} frames!`)
          return
        }

        renderFrame(exportCtx, currentFrame, fps, dims)

        const link = document.createElement('a')
        link.download = `frame-${String(currentFrame).padStart(5, '0')}.png`
        link.href = exportCanvas.toDataURL('image/png')
        link.click()

        currentFrame++
        setProgress(Math.round((currentFrame / totalFrames) * 100))
        setTimeout(captureFrame, 120)
      }

      captureFrame()
    } catch (error) {
      console.error('Export error:', error)
      alert('Export failed: ' + error.message)
      setExporting(false)
    }
  }

  const dims = getExportDimensions()

  return (
    <div className="menu-section">
      <h3>Export</h3>

      <div className="control-group">
        <label className="control-label">
          Duration (sec)
        </label>
        <input
          type="number" min="1" max="120"
          value={config.duration}
          onChange={(e) => updateConfig({ duration: parseInt(e.target.value) || 10 })}
        />
      </div>

      <div className="control-group">
        <label className="control-label">FPS</label>
        <select value={config.fps || 30}
          onChange={(e) => updateConfig({ fps: parseInt(e.target.value) })}>
          <option value={24}>24 fps</option>
          <option value={30}>30 fps</option>
          <option value={60}>60 fps</option>
        </select>
      </div>

      <div className="control-group">
        <label className="control-label">Quality</label>
        <select
          value={config.quality}
          onChange={(e) => updateConfig({ quality: e.target.value })}
        >
          <option value="720p">720p</option>
          <option value="1080p">1080p</option>
          <option value="4k">4K</option>
          <option value="full">Full (2× live)</option>
        </select>
        <span className="value-label">{dims.width}×{dims.height}</span>
      </div>

      <button
        onClick={handleExportVideo}
        disabled={exporting}
      >
        {exporting ? `Exporting… ${progress}%` : 'Export Video (.webm)'}
      </button>

      <button
        onClick={handleExportImages}
        disabled={exporting}
        className="secondary"
      >
        Export Image Sequence
      </button>
      <div className="section-perf-edge" />
    </div>
  )
}

export default ExportPanel
